/**
 * FounderConsole — the single Phase 0 immersive interface.
 *
 * Layout (desktop, left → right):
 *   NavRail (56px) | LeftColumn (300px) | Center (fills) | RightColumn (380px)
 *
 * Left column stacks: SystemStatus / Performance / Capabilities / Diagnostics.
 * Right column stacks: Transcript / MemoryResonance.
 * Center holds: TopStatusBar / QorynOrb / StateLabel / VoiceControlBar.
 *
 * Truth rules per brief:
 *   - Capabilities panel reads /api/capabilities (never fabricated).
 *   - Performance panel shows session-derived counters labeled DEV.
 *   - Memory Resonance panel is clearly PREVIEW (Phase 1 wires real data).
 *   - No model/provider/voice strings in the primary UI.
 *   - No "Qoryn" wordmark top-left. No text inside the orb.
 */

import { useEffect, useRef, useState, useCallback, useMemo, useLayoutEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AlertTriangle, Pause } from "lucide-react";

import { useRealtimeSession } from "@/hooks/useRealtimeSession";
import BackgroundField from "@/components/BackgroundField";
import NavRail from "@/components/NavRail";
import TopStatusBar, { StatusCluster } from "@/components/TopStatusBar";
import QorynHalo from "@/components/QorynHalo";
import { derivePresentation } from "@/lib/haloPresentation";
import { computeCaptionState } from "@/lib/currentTurnCaption";
import StateLabel from "@/components/StateLabel";
import VoiceControlBar from "@/components/VoiceControlBar";
import InlineTranscriptPanel from "@/components/InlineTranscriptPanel";
import PersistenceIndicator from "@/components/PersistenceIndicator";
import SystemStatusPanel from "@/components/panels/SystemStatusPanel";
import PerformanceOverviewPanel from "@/components/panels/PerformanceOverviewPanel";
import CapabilitiesPanel from "@/components/panels/CapabilitiesPanel";
import LeftDiagnosticsPanel from "@/components/panels/LeftDiagnosticsPanel";
import MemoryResonancePanel from "@/components/panels/MemoryResonancePanel";
import QorynCommandPalette, { readVoicePref, writeVoicePref } from "@/components/QorynCommandPalette";
import { fetchHealth, fetchVoices } from "@/lib/api";
import { copyTranscriptMarkdown, downloadTranscriptMarkdown } from "@/lib/transcriptExport";
import { downloadDiagnosticsJson } from "@/lib/diagnosticsExport";
import { useAuth } from "@/auth/AuthProvider";
import { useActiveProject } from "@/context/ActiveProjectContext";

// Halo sizing bounds.
//   Halo `size` is measured from the wrapper's smaller dimension and
//   passed to `<QorynHalo>` as-is (clamped only at the upper bound —
//   HALO_SIZE_MAX). No forced minimum: on narrow / short consoles the
//   halo shrinks with the wrapper. QorynHalo's internal ring geometry
//   still targets a visible diameter of ~200–230 px whenever space
//   permits.
const HALO_SIZE_MAX = 480;

function useHealth() {
    const [health, setHealth] = useState(null);
    useEffect(() => {
        let cancelled = false;
        fetchHealth()
            .then((h) => !cancelled && setHealth(h))
            .catch(() => !cancelled && setHealth({ status: "unreachable" }));
        return () => {
            cancelled = true;
        };
    }, []);
    return health;
}

export default function FounderConsole() {
    // Active project drives the DB session's project_id. If the founder
    // hasn't picked a project yet we still persist — sessions without a
    // project attached show up in the Memory Center under "no project"
    // and can be reassigned later. This matches the backend's optional
    // project_id contract.
    const { activeId: activeProjectId } = useActiveProject();
    const {
        agentState,
        connectionState,
        turns,
        error,
        micMuted,
        audioMode,
        audioModeReason,
        sessionStartedAt,
        diagnostics,
        sessionMeta,
        analyserRef,
        audioElRef,
        persistenceState,
        persistedSessionId,
        persistenceError,
        // The `/app` preview overlay adds a `heldOffline` overlay tile to
        // the hook return; this branch's hook does not yet emit it. When
        // adopted into `/app`, the flag propagates automatically through
        // this destructuring — nothing else needs to change to preserve
        // the HELD overlay in the presentation derivation below.
        heldOffline,
        connect,
        end,
        toggleMute,
        sendText,
        clearTurns,
    } = useRealtimeSession({ projectId: activeProjectId });

    const health = useHealth();
    const backendReady =
        !!health && health.status === "ok" && !!health.openai_api_key_configured;

    // -------------------------------------------------------------------
    // Quick Action Launcher (spotlight-style palette).
    //   • Opens via footer Q button in NavRail, or Cmd/Ctrl+K anywhere.
    //   • Closes via Esc, backdrop, or successful action.
    //   • Voice preference persists in localStorage (readVoicePref /
    //     writeVoicePref helpers exported from QuickActionLauncher).
    //   • Voices list is fetched once on mount from /api/realtime/voices.
    // -------------------------------------------------------------------
    const [launcherOpen, setLauncherOpen] = useState(false);
    const [voices, setVoices] = useState([]);
    const [previewableVoices, setPreviewableVoices] = useState([]);
    const [voicePref, setVoicePref] = useState(() => readVoicePref());

    useEffect(() => {
        let cancelled = false;
        fetchVoices()
            .then((r) => {
                if (cancelled) return;
                setVoices(r?.voices ?? []);
                setPreviewableVoices(r?.previewable ?? []);
            })
            .catch(() => { /* leave empty; launcher shows "voices unavailable" */ });
        return () => { cancelled = true; };
    }, []);

    // Global Cmd/Ctrl+K opens/toggles the launcher. Fires from anywhere on
    // the page, including from inside the composer input (that's the whole
    // point of a command palette). Does NOT interfere with existing
    // transcript shortcuts (Cmd/Ctrl+C/S) — those keys are separate.
    useEffect(() => {
        const onKey = (e) => {
            const mod = e.metaKey || e.ctrlKey;
            if (mod && e.key.toLowerCase() === "k") {
                e.preventDefault();
                setLauncherOpen((v) => !v);
            }
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, []);

    // Launcher action handlers wired to real, existing functionality.
    const handleExportTranscript = useCallback(
        () => downloadTranscriptMarkdown(turns, sessionStartedAt),
        [turns, sessionStartedAt],
    );
    const handleCopyTranscript = useCallback(
        () => copyTranscriptMarkdown(turns, sessionStartedAt),
        [turns, sessionStartedAt],
    );
    const handleClearTranscript = useCallback(() => clearTurns(), [clearTurns]);
    const handleDiagnosticsSnapshot = useCallback(() => {
        const base = sessionStartedAt ?? (diagnostics[0]?.ts ?? null);
        downloadDiagnosticsJson(diagnostics, base);
    }, [diagnostics, sessionStartedAt]);
    const handleStartSession = useCallback(() => {
        // End current if live, then connect with the preferred voice (if set).
        if (connectionState === "open" || connectionState === "connecting") {
            end();
            // Small delay so the underlying WebRTC teardown completes before
            // we start a fresh session — mirrors the "restart" pattern used
            // in the voice submenu.
            setTimeout(() => connect({ voice: voicePref ?? undefined }), 200);
        } else {
            connect({ voice: voicePref ?? undefined });
        }
        return true;
    }, [connect, end, connectionState, voicePref]);
    const handleRestartSessionWithVoice = useCallback((voice) => {
        end();
        setTimeout(() => connect({ voice }), 200);
    }, [connect, end]);
    const handleSetVoicePref = useCallback((voice) => {
        writeVoicePref(voice);
        setVoicePref(voice);
    }, []);

    // Auth: sign-out for the Command Palette.
    const { signOut, user: authUser } = useAuth();
    const handleSignOut = useCallback(async () => {
        try {
            end();
        } catch { /* noop */ }
        await signOut();
    }, [end, signOut]);

    // Output-audio (Qoryn's voice) mute state — controlled by the top-
    // right speaker button in StatusCluster. Persists across sessions
    // via localStorage. When the audio element is (re)created by the
    // hook on each new connection, we re-apply the muted flag so the
    // user's preference is honoured immediately.
    const OUTPUT_MUTED_KEY = "qoryn:v0:output-muted";
    const [outputMuted, setOutputMuted] = useState(() => {
        try { return localStorage.getItem(OUTPUT_MUTED_KEY) === "1"; }
        catch { return false; }
    });
    const toggleOutputMute = () => {
        setOutputMuted((prev) => {
            const next = !prev;
            try { localStorage.setItem(OUTPUT_MUTED_KEY, next ? "1" : "0"); } catch { /* noop */ }
            return next;
        });
    };
    // Apply the muted preference to the current audio element whenever
    // either changes. We poll for a short window after a new session
    // starts because the hook creates the audio element async on connect,
    // and refs don't trigger re-renders. A 4s poll @ 200ms is cheap and
    // guarantees we catch the element within one frame of it existing.
    useEffect(() => {
        const apply = () => {
            const el = audioElRef?.current;
            if (el) {
                el.muted = outputMuted;
                return true;
            }
            return false;
        };
        if (apply()) return;
        let ticks = 0;
        const int = setInterval(() => {
            if (apply() || ++ticks > 20) clearInterval(int);
        }, 200);
        return () => clearInterval(int);
    }, [outputMuted, audioElRef, connectionState]);

    // Engine stats — historically populated by QorynOrbWebGL's RAF loop.
    // With QorynHalo (canvas 2D, RAF owned by the component) there is no
    // external stats surface; the diagnostics panel receives the ref
    // unchanged for API compatibility. The obsolete Visual Intensity
    // slider (which only ever affected the removed WebGL engine) is NOT
    // wired here — LeftDiagnosticsPanel hides its slider when no
    // `onIntensityChange` handler is supplied, keeping the diagnostics
    // panel honest instead of exposing a persisted-but-inert control.
    const engineStatsRef = useRef(null);

    // -------------------------------------------------------------------
    // Adaptive transcript column (iteration 16).
    //   • Pre-conversation (0 turns): compact 240px so the centre orb
    //     dominates and the console feels calm.
    //   • First real turn arrives: auto-expand to the full width in a
    //     300ms transition (respects prefers-reduced-motion via CSS).
    //   • Manual toggle in the transcript panel header remains available
    //     during a live conversation for founders who prefer more centre
    //     stage room.
    //   • Behaviour is PURELY visual — audio capture, VAD, ASR, and
    //     normalization continue regardless of expansion state.
    // -------------------------------------------------------------------
    const [transcriptExpanded, setTranscriptExpanded] = useState(false);
    const autoExpandedRef = useRef(false);
    useEffect(() => {
        if (turns.length > 0 && !autoExpandedRef.current) {
            autoExpandedRef.current = true;
            setTranscriptExpanded(true);
        }
        if (turns.length === 0) {
            // Reset the auto-expand latch so a fresh conversation gets a
            // fresh compact→expanded animation. Manual state is preserved
            // only within a session.
            autoExpandedRef.current = false;
        }
    }, [turns.length]);

    // -------------------------------------------------------------------
    // Shared presentation state (Voice UI Halo).
    //   Both the QorynHalo visual and the StateLabel copy derive from
    //   this ONE value, computed synchronously from the hook state, so
    //   the two can never disagree. Every active presentation is gated
    //   on: (open connection) AND (audioMode === "live") AND (not held).
    //   `heldOffline` is destructured defensively — on this branch the
    //   hook does not emit it (undefined ⇒ not held); when this code is
    //   adopted into `/app`, the overlay's HELD tile propagates through
    //   the same derivation without additional wiring.
    // -------------------------------------------------------------------
    const presentation = useMemo(
        () => derivePresentation({
            agentState,
            connectionState,
            micMuted,
            outputMuted,
            audioMode,
            held: !!heldOffline?.active,
        }),
        [agentState, connectionState, micMuted, outputMuted, audioMode, heldOffline],
    );

    // -------------------------------------------------------------------
    // Current-turn caption (Voice UI Halo).
    //   Only the LATEST turn drives the caption:
    //     • If the latest turn is an assistant turn, its full
    //       `normalized ?? raw` text is shown visually beneath the
    //       halo — multi-line, bounded height, scrollable when long.
    //     • If the latest turn is a user turn, the caption is CLEARED —
    //       we never show a stale assistant response after a newer user
    //       turn has arrived.
    //     • If there are no turns, nothing is shown.
    //   Accessibility (Root remainder C):
    //     • The visible caption element is `aria-live="off"` so its
    //       text is present in the a11y tree for narrow-screen AT
    //       users (where the right-column transcript is hidden
    //       `md:flex`) but is NOT auto-announced token-by-token as
    //       partial deltas stream in.
    //     • A parallel SR-only element carries `role="status"` /
    //       `aria-live="polite"` and is updated ONLY when a completed
    //       assistant turn is newly reached, giving a bounded
    //       announcement per completed turn.
    //     • Long text is never discarded — the whole normalized turn
    //       is available; the visible container scrolls.
    // -------------------------------------------------------------------
    // See `frontend/src/lib/currentTurnCaption.js` for the caption
    // derivation rules. Extracted from FC so the caption behavior can
    // be unit-tested (Root remainder C).
    const captionState = useMemo(() => computeCaptionState(turns), [turns]);

    // Track which completed turn has been announced to avoid re-firing
    // the polite aria-live region on unrelated re-renders.
    const lastAnnouncedTurnRef = useRef(null);
    const [srCaption, setSrCaption] = useState("");
    useEffect(() => {
        const { completedText, completedTurnId } = captionState;
        if (completedTurnId && completedTurnId !== lastAnnouncedTurnRef.current) {
            lastAnnouncedTurnRef.current = completedTurnId;
            setSrCaption(completedText);
        } else if (!completedTurnId) {
            if (lastAnnouncedTurnRef.current !== null) {
                lastAnnouncedTurnRef.current = null;
                setSrCaption("");
            }
        }
    }, [captionState]);

    // -------------------------------------------------------------------
    // Responsive halo size — the wrapper's smaller dimension bounds the
    // canvas so the halo never overflows a narrow or short console. No
    // forced minimum: the halo may render smaller than 260 px on very
    // narrow viewports; the layout adds vertical scroll when needed to
    // keep the state label and voice controls reachable.
    // -------------------------------------------------------------------
    const haloWrapperRef = useRef(null);
    const [haloSize, setHaloSize] = useState(HALO_SIZE_MAX);
    useLayoutEffect(() => {
        const el = haloWrapperRef.current;
        if (!el || typeof ResizeObserver === "undefined") return;
        const measure = () => {
            const rect = el.getBoundingClientRect();
            const side = Math.floor(Math.min(rect.width, rect.height));
            // No forced minimum — a wrapper smaller than the design
            // maximum yields a proportionally smaller halo.
            const clamped = Math.max(0, Math.min(HALO_SIZE_MAX, side));
            setHaloSize(clamped);
        };
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    return (
        <div
            data-testid="founder-console"
            data-agent-state={agentState}
            data-connection-state={connectionState}
            className="relative h-screen w-full overflow-hidden bg-qoryn-bg text-qoryn-text flex"
        >
            <BackgroundField />

            <NavRail onOpenLauncher={() => setLauncherOpen(true)} />

            {/* ---- Left intelligence column ------------------------------- */}
            <aside
                data-testid="left-column"
                className="hidden xl:flex flex-col gap-3 w-[300px] shrink-0 p-4 relative z-10 h-screen overflow-y-auto hide-scrollbar min-h-0"
            >
                <SystemStatusPanel
                    agentState={agentState}
                    connectionState={connectionState}
                    backendReady={backendReady}
                />
                <PerformanceOverviewPanel
                    turns={turns}
                    diagnostics={diagnostics}
                    sessionStartedAt={sessionStartedAt}
                />
                <CapabilitiesPanel />
                <LeftDiagnosticsPanel
                    diagnostics={diagnostics}
                    sessionStartedAt={sessionStartedAt}
                    engineStatsRef={engineStatsRef}
                />
            </aside>

            {/* ---- Center stage ------------------------------------------- */}
            <main
                data-testid="center-stage"
                className="relative z-10 flex-1 min-w-0 min-h-0 flex flex-col h-screen"
            >
                <TopStatusBar />

                {/* Backend not ready banner */}
                {health && !backendReady && (
                    <div
                        data-testid="backend-warning"
                        className="mx-6 mb-2 qoryn-panel rounded-full px-4 py-2 flex items-center gap-2 self-start shrink-0"
                    >
                        <AlertTriangle className="h-3.5 w-3.5 text-yellow-400" />
                        <span className="font-mono-ui text-[10px] tracking-widest uppercase text-qoryn-textDim">
                            {health.status !== "ok"
                                ? "Backend unreachable"
                                : "Voice service not configured"}
                        </span>
                    </div>
                )}

                {/* Halo + caption + state + controls.
                    QorynHalo mounts in the center visual region with a
                    responsive footprint (bounded by the wrapper's
                    smaller dimension — never overflows a narrow or
                    short console). The current-turn caption sits
                    directly under the halo. State label and controls
                    follow as fixed rows beneath so they never clip. */}
                <div className="flex-1 min-h-0 flex flex-col items-center justify-center px-6 relative">
                    <div
                        ref={haloWrapperRef}
                        data-testid="halo-mount"
                        data-halo-size={String(haloSize)}
                        className="relative flex items-center justify-center flex-1 min-h-0 w-full"
                        style={{ maxWidth: HALO_SIZE_MAX, maxHeight: HALO_SIZE_MAX }}
                    >
                        <QorynHalo
                            presentation={presentation}
                            analyserRef={analyserRef}
                            sessionKey={sessionStartedAt ?? null}
                            size={haloSize}
                        />
                    </div>

                    {/* Current-turn caption — multi-line, bounded height
                        with vertical scroll for long text. aria-live is
                        "off" so the text is present in the a11y tree
                        for narrow-screen AT users but never announced
                        token-by-token. A parallel SR-only status region
                        announces only completed assistant turns. */}
                    <div
                        className="mt-1 mb-2 max-w-[600px] w-full px-4 shrink-0 relative"
                        style={{ zIndex: 10 }}
                    >
                        {captionState.visualText && (
                            <div
                                data-testid="halo-current-turn-caption"
                                aria-live="off"
                                className="font-sans text-[13px] leading-[1.45] text-qoryn-textDim text-center whitespace-pre-wrap max-h-[86px] overflow-y-auto"
                            >
                                {captionState.visualText}
                            </div>
                        )}
                    </div>
                    <div
                        data-testid="halo-current-turn-sr"
                        role="status"
                        aria-live="polite"
                        aria-atomic="true"
                        className="sr-only"
                    >
                        {srCaption}
                    </div>

                    {/* State label sits between the caption and the control bar */}
                    <div className="mt-2 mb-4 relative shrink-0" style={{ zIndex: 10 }}>
                        <StateLabel
                            agentState={agentState}
                            presentation={presentation}
                        />
                    </div>

                    {/* Compact voice control — fixed row, guaranteed visible */}
                    <div className="relative shrink-0 pb-6" style={{ zIndex: 10 }}>
                        <VoiceControlBar
                            agentState={agentState}
                            micMuted={micMuted}
                            audioMode={audioMode}
                            audioModeReason={audioModeReason}
                            onStart={() => connect({ voice: voicePref ?? undefined })}
                            onEnd={end}
                            onToggleMute={toggleMute}
                            onSendText={sendText}
                        />
                    </div>
                </div>

                {/* Non-blocking error tile above the dock area.
                    Mutually exclusive with the paused tile below: the
                    hook never sets `error` when it classifies a held
                    response, so both cannot be truthy simultaneously. */}
                <AnimatePresence>
                    {!heldOffline?.active && error && (
                        <motion.div
                            key="err"
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 16 }}
                            data-testid="error-tile"
                            className="fixed bottom-24 left-1/2 -translate-x-1/2 z-40 qoryn-panel rounded-xl px-4 py-3 max-w-md flex items-start gap-3"
                        >
                            <AlertTriangle className="h-4 w-4 text-red-400 mt-0.5 shrink-0" />
                            <div className="flex flex-col gap-1 min-w-0">
                                <span className="font-mono-ui text-[10px] tracking-widest uppercase text-red-300">
                                    Session error
                                </span>
                                <span className="font-sans text-xs text-qoryn-textDim break-words">
                                    {error}
                                </span>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Neutral PAUSED tile — rendered ONLY when the hook has
                    classified a strict 503 + detail.state === HELD signal.
                    Deliberately visually distinct from the error tile:
                    no red, no "Session error" label, no raw JSON. Text
                    is fixed copy from the classifier (heldOffline module)
                    — never a stringified payload, never env-var names,
                    never governance jargon. */}
                <AnimatePresence>
                    {heldOffline?.active && (
                        <motion.div
                            key="paused"
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 16 }}
                            data-testid="session-paused-tile"
                            data-held-state={heldOffline.state}
                            className="fixed bottom-24 left-1/2 -translate-x-1/2 z-40 qoryn-panel rounded-xl px-4 py-3 max-w-md flex items-start gap-3"
                        >
                            <Pause className="h-4 w-4 text-qoryn-textDim mt-0.5 shrink-0" />
                            <div className="flex flex-col gap-1 min-w-0">
                                <span
                                    data-testid="session-paused-headline"
                                    className="font-mono-ui text-[10px] tracking-widest uppercase text-qoryn-textDim"
                                >
                                    {heldOffline.headline}
                                </span>
                                <span
                                    data-testid="session-paused-supporting"
                                    className="font-sans text-xs text-qoryn-textDim break-words"
                                >
                                    {heldOffline.supporting}
                                </span>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </main>

            {/* ---- Right column ------------------------------------------- */}
            {/* Fixed approved width (iteration 16b — reverted from adaptive-column
                after user bug report: narrowing the whole column also squeezed
                Memory Resonance and forced the status cluster to wrap. The
                compact "pre-conversation" behaviour now lives inside the
                transcript panel itself as a HEIGHT reduction, so Memory
                Resonance always keeps its full width and the status cluster
                always sits on one line. */}
            <aside
                data-testid="right-column"
                data-transcript-expanded={transcriptExpanded ? "true" : "false"}
                className="hidden md:flex flex-col gap-3 shrink-0 p-4 relative z-10 h-screen min-h-0 md:w-[320px] xl:w-[380px]"
            >
                <StatusCluster
                    agentState={agentState}
                    connectionState={connectionState}
                    sessionStartedAt={sessionStartedAt}
                    outputMuted={outputMuted}
                    onToggleOutputMute={toggleOutputMute}
                />
                {/* Persistence indicator — truthful, right-aligned under
                    the status cluster. Only renders while there's an
                    active session (state !== "idle"). Text mirrors the
                    LAST observed API response; never optimistic. */}
                <div className="flex justify-end -mt-1">
                    <PersistenceIndicator
                        state={persistenceState}
                        error={persistenceError}
                        sessionId={persistedSessionId}
                    />
                </div>
                {/* Transcript: flex-1 (full remaining height) when expanded,
                    fixed compact height when collapsed pre-conversation. The
                    change is HEIGHT only — the column width stays constant so
                    Memory Resonance below never gets squeezed. */}
                <div
                    className={`min-h-0 transition-[flex-grow,height] duration-300 ease-out ${
                        transcriptExpanded ? "flex-1" : "flex-none h-16"
                    }`}
                >
                    <InlineTranscriptPanel
                        turns={turns}
                        sessionStartedAt={sessionStartedAt}
                        expanded={transcriptExpanded}
                        onToggleExpanded={() => setTranscriptExpanded((v) => !v)}
                    />
                </div>
                <MemoryResonancePanel turns={turns} />
            </aside>

            {/* ---- Qoryn Command Palette (spotlight-style) ----------------- */}
            <QorynCommandPalette
                open={launcherOpen}
                onClose={() => setLauncherOpen(false)}
                turns={turns}
                sessionStartedAt={sessionStartedAt}
                diagnostics={diagnostics}
                connectionState={connectionState}
                voices={voices}
                previewable={previewableVoices}
                currentVoice={sessionMeta?.voice ?? null}
                voicePref={voicePref}
                onSetVoicePref={handleSetVoicePref}
                onExportTranscript={handleExportTranscript}
                onCopyTranscript={handleCopyTranscript}
                onClearTranscript={handleClearTranscript}
                onDiagnosticsSnapshot={handleDiagnosticsSnapshot}
                onStartSession={handleStartSession}
                onRestartSessionWithVoice={handleRestartSessionWithVoice}
                authEmail={authUser?.email ?? null}
                onSignOut={handleSignOut}
            />
        </div>
    );
}
